import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { syncPushSubscription } from '../utils/push'

/**
 * Two small jobs that both need to sit inside the Router:
 *
 * 1. Route in-place when a push notification is tapped. push-sw.js prefers focusing an
 *    already-open window over spawning a second one, and tells that window where to go
 *    via postMessage -- this turns that into a client-side navigation, so tapping a
 *    notification lands on the rental instead of doing a full page reload.
 *
 * 2. Re-register the push subscription on login/app load, which is what repairs a
 *    rotated subscription (the service worker can't do it -- the API needs the auth
 *    token, which lives in localStorage and is unreachable from a worker).
 */
export default function PushBridge() {
  const navigate = useNavigate()
  const { user } = useAuth()

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'push:navigate') return
      const url = event.data.url
      if (typeof url !== 'string' || !url.startsWith('/')) return
      navigate(url)
    }

    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [navigate])

  useEffect(() => {
    if (!user) return
    syncPushSubscription()
  }, [user])

  return null
}
